import React, { Component, UIEvent } from 'react';
import styled from 'styled-components';
import { Loader } from '../loader';
import { hasReachedEnd } from '../../utils/infinite-scroll';

export type Props = {
  className?: string;
  endZoneSize?: number;
  isLoading: boolean;
  nbElementsFetched?: number;
  nbElementsTotal?: number;
  onEndReached: () => void;
  hasSidePadding?: boolean;
  paddingTop?: number;
  paddingBottom?: number;
  hasOverflow?: boolean;
  watchWindowScroll?: boolean;
  pageName?: string;
};

class InfiniteScroller extends Component<Props> {
  componentDidMount() {
    if (this.props.watchWindowScroll)
      window.addEventListener('scroll', this.handleWindowScroll);
  }

  componentWillUnmount() {
    if (this.props.watchWindowScroll)
      window.removeEventListener('scroll', this.handleWindowScroll);
  }

  public render() {
    const {
      children,
      className,
      isLoading,
      hasSidePadding = false,
      paddingTop = 0,
      paddingBottom = 0,
      hasOverflow = false,
      pageName,
    } = this.props;

    return (
      <SInfiniteScrollerContainer
        className={className}
        hasSidePadding={hasSidePadding}
        paddingTop={paddingTop}
        paddingBottom={paddingBottom}
        hasOverflow={hasOverflow}
        onScroll={this.onScroll}
        data-test={pageName ? `${pageName}-page` : undefined}
      >
        {children}
        <StyledLoader isLoading={isLoading} />
      </SInfiniteScrollerContainer>
    );
  }

  handleWindowScroll = (event: any) => {
    if (event && event.target && event.target.documentElement)
      this.checkScrollPosition(
        event.target.documentElement.scrollTop,
        event.target.documentElement.clientHeight,
        event.target.documentElement.scrollHeight
      );
  };

  private onScroll = ({ currentTarget }: UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = currentTarget;

    this.checkScrollPosition(scrollTop, clientHeight, scrollHeight);
  };

  checkScrollPosition(
    scrollTop: number,
    clientHeight: number,
    scrollHeight: number
  ) {
    const {
      endZoneSize = 500,
      isLoading,
      nbElementsFetched = 0,
      nbElementsTotal = 0,
      onEndReached,
    } = this.props;

    const isAtBottom = hasReachedEnd({
      scrollTop,
      scrollHeight,
      clientHeight,
      endZoneSize,
    });

    const hasFetchedAll = nbElementsFetched >= nbElementsTotal;

    if (isAtBottom && !isLoading && !hasFetchedAll) onEndReached();
  }
}

const SInfiniteScrollerContainer = styled.div<{
  hasSidePadding: boolean;
  paddingTop: number;
  paddingBottom: number;
  hasOverflow: boolean;
}>`
  padding: 0
    ${({ theme, hasSidePadding }) => (hasSidePadding ? theme.space.large : 0)};
  padding-top: ${({ paddingTop }) => `${paddingTop}px`};
  padding-bottom: ${({ paddingBottom }) => `${paddingBottom}px`};
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: ${({ hasOverflow }) => (hasOverflow ? 'visible' : 'auto')};
  -webkit-overflow-scrolling: touch;
`;

const StyledLoader = styled(Loader)`
  margin: ${({ theme }) => `${theme.space.medium} 0`};
`;

export default InfiniteScroller;
